import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card, StatCard } from '@/components/ui/Card'
import { Pagination, PageInfo } from '@/components/ui/Pagination'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { OcrStatusBadge } from '@/components/shared/StatusBadge'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useOcrTasks, useOcrResults } from '@/hooks/useProOps'
import { useState } from 'react'
import type { OcrTask, OcrResult } from '@/lib/pocketbase'

export function OcrPage() {
  const [activeTab, setActiveTab] = useState('tasks')
  const [searchParams, setSearchParams] = useSearchParams()
  const page = parseInt(searchParams.get('page') || '1')

  const { data: tasksData, isLoading: tasksLoading } = useOcrTasks({ page, perPage: 20 })
  const { data: resultsData, isLoading: resultsLoading } = useOcrResults({ page, perPage: 20 })

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  // Calculate stats
  const stats = tasksData ? {
    total: tasksData.totalItems,
    pending: tasksData.items.filter((t) => t.status === 'pending').length,
    processing: tasksData.items.filter((t) => t.status === 'processing').length,
    completed: tasksData.items.filter((t) => t.status === 'done').length,
    failed: tasksData.items.filter((t) => t.status === 'failed').length,
  } : null

  return (
    <div>
      <PageHeader
        title="งาน OCR"
        description="ติดตามการดึงข้อความจากเอกสาร"
      />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="งานทั้งหมด"
            value={stats.total}
            icon={<Icon icon="solar:scanner-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="รอดำเนินการ"
            value={stats.pending}
            icon={<Icon icon="solar:clock-circle-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="กำลังประมวลผล"
            value={stats.processing}
            icon={<Icon icon="solar:refresh-circle-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="เสร็จสิ้น"
            value={stats.completed}
            icon={<Icon icon="solar:check-circle-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="ล้มเหลว"
            value={stats.failed}
            icon={<Icon icon="solar:close-circle-line-duotone" className="w-5 h-5" />}
          />
        </div>
      )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="tasks">งาน</TabsTrigger>
          <TabsTrigger value="results">ผลลัพธ์</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks">
          {tasksData && (
            <div className="mb-4">
              <PageInfo
                currentPage={tasksData.page}
                pageSize={tasksData.perPage}
                totalItems={tasksData.totalItems}
              />
            </div>
          )}

          {tasksLoading && <LoadingState />}

          {tasksData && tasksData.items.length === 0 && (
            <Card className="p-8 text-center">
              <Icon icon="solar:scanner-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900">ไม่พบงาน OCR</h3>
            </Card>
          )}

          {tasksData && tasksData.items.length > 0 && (
            <>
              <div className="flex flex-col gap-3">
                {tasksData.items.map((task) => (
                  <OcrTaskCard key={task.id} task={task} />
                ))}
              </div>
              <div className="mt-6">
                <Pagination
                  currentPage={tasksData.page}
                  totalPages={tasksData.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="results">
          {resultsData && (
            <div className="mb-4">
              <PageInfo
                currentPage={resultsData.page}
                pageSize={resultsData.perPage}
                totalItems={resultsData.totalItems}
              />
            </div>
          )}

          {resultsLoading && <LoadingState />}

          {resultsData && resultsData.items.length === 0 && (
            <Card className="p-8 text-center">
              <Icon icon="solar:document-text-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900">ไม่พบผลลัพธ์ OCR</h3>
            </Card>
          )}

          {resultsData && resultsData.items.length > 0 && (
            <>
              <div className="flex flex-col gap-3">
                {resultsData.items.map((result) => (
                  <OcrResultCard key={result.id} result={result} />
                ))}
              </div>
              <div className="mt-6">
                <Pagination
                  currentPage={resultsData.page}
                  totalPages={resultsData.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function OcrTaskCard({ task }: { task: OcrTask }) {
  const attachment = task.expand?.attachment_id as any
  const displayDate = attachment?.published_at

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <OcrStatusBadge status={task.status as any} />
            <span className="font-mono text-sm text-slate-500">
              #{task.id.slice(0, 8)}
            </span>
          </div>
          {attachment && (
            <p className="font-medium text-slate-900 mt-2 truncate">
              {attachment.file}
            </p>
          )}
          {task.error_message && (
            <p className="mt-2 text-sm text-rose-600 bg-rose-50 rounded-lg p-2">
              {task.error_message}
            </p>
          )}
        </div>
        <div className="text-right text-sm text-slate-500">
          {displayDate ? (
            <DateDisplay date={displayDate} format="relative" />
          ) : (
            <span>-</span>
          )}
        </div>
      </div>
    </Card>
  )
}

function OcrResultCard({ result }: { result: OcrResult }) {
  const attachment = result.expand?.attachment_id as any
  const displayDate = attachment?.published_at

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {attachment && (
            <p className="font-medium text-slate-900 truncate">
              {attachment.file}
            </p>
          )}
          {result.extracted_text && (
            <p className="mt-2 text-sm text-slate-600 line-clamp-3 bg-slate-50 rounded-lg p-3">
              {result.extracted_text}
            </p>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
            {result.page_count && <span>{result.page_count} หน้า</span>}
            {result.confidence && (
              <span>ความมั่นใจ {(result.confidence * 100).toFixed(0)}%</span>
            )}
          </div>
        </div>
        <div className="text-right text-sm text-slate-500">
          {displayDate ? (
            <DateDisplay date={displayDate} format="relative" />
          ) : (
            <span>-</span>
          )}
        </div>
      </div>
    </Card>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="animate-pulse">
          <div className="h-4 w-24 bg-slate-200 rounded" />
          <div className="h-4 w-48 bg-slate-200 rounded mt-2" />
        </Card>
      ))}
    </div>
  )
}
